import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  AfterViewInit,
  OnDestroy,
  QueryList,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import {
  filter,
  tap,
  take,
  switchMap,
  takeUntil,
  delay,
  distinctUntilChanged,
  timeout,
  catchError,
} from 'rxjs/operators';
import { PageService } from '@services/page.service';
import { UserService, PENDING_REDIRECT_URL_KEY, isAuthFlowUrl, isSsoFlowUrl } from '@services/user.service';
import { LoadingPageScreenService } from '@services/loading-page-screen.service';
import { CookieService } from 'ngx-cookie-service';
import { CommentingService } from './scaffold/services/commenting.service';
import { ScaffoldDialogService } from './framework/dialog/scaffold-dialog.service';
import { PropertiesService } from './scaffold/services/properties.service';
import {
  NotificationsService,
  NotificationStatus,
} from './services/notifications.service';
import { ItemsService } from './scaffold/services/items.service';
import { EnvironmentService, Features } from '@services/environment.service';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { IframeService } from '@services/iframe.service';
import { MainSidebarService } from '@services/main-sidebar.service';
import { TranslateService } from '@ngx-translate/core';
import { TranslationService } from '@services/translation.service';
import { HttpClient } from '@angular/common/http';
import { OverlayService } from '@services/overlay.service';
import { PageTitleService } from '@services/page-title.service';
import { MenuListService } from '@services/menu-list.service';
import { IntercomService } from '@services/intercom.service';
import { VerifyModalComponent } from './account/profile/verify-modal/verify-modal.component';
import {
  NotificationMessageData,
  NotificationMessageIcon,
} from './framework/notification-sidebar/notification-sidebar.component';
import { CognitoDomainService } from '@services/cognito-domain.service';
import { IdentityProviderService } from './services/identity-provider.service';
import { WebSocketService } from './services/web-socket.service';
import { TourService } from '@services/tour.service';
import { QuickHelpComponent } from '@scaffold/dialogs/quick-help/quick-help.component';
import * as Sentry from '@sentry/angular';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  features = [];
  activeFeature;
  title = 'E2-Web';
  /** Status of the top menu bar*/
  hideMenu = false;
  loginMfaSetupRequired$ = this.userService.loginMfaSetupRequired$;
  /** User details*/
  userInfo = null;
  /** Revious path*/
  prevPath = '';
  /** Environment Type*/
  env;
  /** State of Scaffold menu*/
  private _navState = ['collapsed', 'compacted'];
  sideNavState = 'collapsed';
  private cognitoDomain;
  /**Settings object for currently selected  properties item*/
  propertiesItem;
  /**Disable the properties panel if currently displayed*/
  propertiesDisabled;
  /** Content of left Scaffold menu*/
  globalLeftMenuItems = [];
  globalLeftMenuItemsError: boolean;
  footerMenu = [];
  selectedType;
  darkLogoError = false;
  loadingMenu: boolean = true;
  personalMenu: any[] = [
    {
      routerLink: '/profile',
      label: this.translation.getTranslatedContent('profile', 'Profile'),
    },
    {
      routerLink: '/settings',
      label: this.translation.getTranslatedContent(
        'user_settings',
        'User Settings'
      ),
    },
    {
      routerLink: '/reset-password',
      label: this.translation.getTranslatedContent(
        'reset_password',
        'Reset Password'
      ),
    },
    {
      label: this.translation.getTranslatedContent('logout', 'Logout'),
      command: () => this.logout(),
      skipLocationChange: true,
    },
  ];

  private avoidSSORedirect: boolean = false;
  private destroy$ = new Subject<void>();
  private intercomBootRequestId = 0;
  private menuInitialized = false;
  displayAutosave;
  notificationStatus$: Observable<NotificationStatus>;
  activeNotificationCount: number = 0;
  instanceDetails;
  translate$: any;
  hasAddedWizardFooter = false;
  notifiedUnverifiedEmail: boolean;
  userAttributes$;
  loggedIn: any;
  cognitoSsoUrl: string;
  initalSsoAuthenticated;
  sloUrl: string;
  loadingPage$;
  showLoadingContentOverlay: boolean = false;
  mfacheck$;
  @ViewChildren('scaffoldMenuToggle')
  scaffoldMenuToggles: QueryList<ElementRef>;
  @ViewChildren('left-menu-button-admin') adminButton: QueryList<ElementRef>;
  public isDarkMode = false;
  public headerButtonTabIndex: number = -1;
  userAvatarImageError = false;
  private headerButtonFocusHandler: (event: FocusEvent) => void;
  private preventFocusTimeout: any;
  public displayName: string;
  onUserAvatarError() {
    this.userAvatarImageError = true;
    this.cd.markForCheck();
  }

  /** Overlay styles for the content loading overlay; left aligns with sidebar so it does not cover the nav. */
  get loadingContentOverlayStyles(): { [key: string]: string | number } {
    const leftByState: Record<string, string> = {
      collapsed: 'var(--sidebar-width-collapsed)',
      compacted: 'var(--sidebar-width-compact)',
      expanded: 'var(--sidebar-width-expanded)',
    };
    return {
      opacity: 1,
      position: 'fixed',
      top: 'var(--header-height)',
      left: leftByState[this.sideNavState] ?? 'var(--sidebar-width-collapsed)',
      right: '0',
      bottom: '0',
    };
  }

  constructor(
    private pageTitleService: PageTitleService,
    private loadingPageScreenService: LoadingPageScreenService,
    private translation: TranslationService,
    private translate: TranslateService,
    public dialog: ScaffoldDialogService,

    private mainSidebarService: MainSidebarService,
    public iframeService: IframeService,
    private page: PageService,
    private router: Router,
    private userService: UserService,
    private cookieService: CookieService,
    public commenting: CommentingService,
    private properties: PropertiesService,
    public notificationService: NotificationsService,
    public itemsService: ItemsService,
    public environmentService: EnvironmentService,
    private activatedRoute: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private http: HttpClient,
    private overlay: OverlayService,
    private menuListService: MenuListService,
    private intercomService: IntercomService,
    private cognitoDomainService: CognitoDomainService,
    private identityProviderService: IdentityProviderService,
    private webSocketService: WebSocketService,
    private tourService: TourService,
    private viewContainerRef: ViewContainerRef
  ) {
    // translate.setFallbackLang('en-US');
  }

  ngOnInit() {
    this.isDarkMode = document.documentElement.classList.contains('dark-theme');
    this.checkToOpenSidebar();
    this.mainSidebarService.updateState(false);
    this.checkForMfaEnabled();
    this.initSocket();
    this.userService.watchAuthenticationCookie();
    this.overlay.open(
      { overlayStyles: { opacity: '.95' }, showSpinner: false },
      'translate'
    );
    this.environmentService.setInstanceCookies();
    this.environmentService
      .getInstanceDetails()
      .pipe(
        filter(() => this.menuInitialized),
        distinctUntilChanged(
          (a, b) =>
            a.system_type === b.system_type &&
            a.no_self_serve === b.no_self_serve
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(details => this.rebuildMenuForSystemTypeChange(details));

    // ensure SSO URL is detected
    setTimeout(() => {
      try {
        this.cognitoDomainService.refreshSsoUrl();
      } catch (error) {
        console.warn('AppComponent: Error refreshing SSO URL:', error);
      }
    }, 500);

    this.loadingPage$ = this.loadingPageScreenService.loadingPage$.pipe(
      tap(isLoadingPage => (this.showLoadingContentOverlay = isLoadingPage))
    );

    this.initCognitoSSOLogic();
    this.translation
      .loadTranslations(this.translation.browserLanguage)
      .subscribe(() => {
        this.cd.markForCheck();
        this.overlay.close('translate');
      });

    this.translate$ = this.translate.onLangChange.pipe(
      tap(() => {
        {
          const profileText = this.translation.getTranslatedContent(
            'profile',
            'Profile'
          );
          const userSettingsText = this.translation.getTranslatedContent(
            'user_settings',
            'User Settings'
          );
          const resetText = this.translation.getTranslatedContent(
            'reset_password',
            'Reset Password'
          );
          const logOutText = this.translation.getTranslatedContent(
            'logout',
            'Logout'
          );
          this.personalMenu = [
            { routerLink: '/profile', label: profileText },
            { routerLink: '/settings', label: userSettingsText },
            { routerLink: '/reset-password', label: resetText },
            {
              label: logOutText,
              command: () => this.logout(),
              skipLocationChange: true,
            },
          ];
        }
      })
    );

    this.mainSidebarService.openState$.pipe(delay(500)).subscribe(state => {
      // avoid this ever being opend from an iframe
      this.sideNavState = this.iframeService.isInIframe
        ? this._navState[0]
        : state
          ? this._navState[1]
          : this._navState[0];
    });

    this.properties.watchProperties().subscribe(item => {
      this.propertiesItem = item;
    });
    this.properties.propertiesDisabledChange().subscribe(status => {
      this.propertiesDisabled = status;
    });

    if (this.environmentService.environment.suppressLog) {
      if (window) {
        window.console.log = function () {};
      }
    }
    if (!this.environmentService.environment.production) {
      this.env = 'Development';
    }
    if (location.toString().match(/\-test/)) {
      this.env = 'Test';
    }
    // Set up listeners for updates to the different menus
    this.page.hideMenuUpdates().subscribe(hideMenuStatus => {
      setTimeout(() => {
        this.hideMenu = hideMenuStatus;
      });
    });
    this.setupSideMenu();
    // this.router.events
    //   .pipe(
    //     filter(event => event instanceof NavigationEnd),
    //     takeUntil(this.destroy$)
    //   )
    //   .subscribe(() => {
    //     this.menuListService
    //       .patchCoursesMenuChildren(
    //         this.globalLeftMenuItems,
    //         this.instanceDetails,
    //         this.activeFeature
    //       )
    //       .subscribe(() => this.cd.detectChanges());
    //   });
    this.userService
      .getRoleDetails()
      .pipe(takeUntil(this.destroy$))
      .subscribe(roleData => {
        this.userInfo = roleData?.data ?? null;
        this.displayName =
          this.userInfo?.given_name?.trim() ||
          this.userInfo?.first_name?.trim() ||
          this.userInfo?.cognito_username;
        if (this.userInfo) this.userAvatarImageError = false;
        this.cd.markForCheck();
      });
    // Setup event listener to page change
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.activatedRoute.queryParams.subscribe(d => {
          const currentPath = this.router.url.split('?')[0];

          if (currentPath !== this.prevPath) {
            // Generate the content of the Scaffold menu
            this.hideMenu = false;
          }
          this.prevPath = currentPath;
        });
        this.intercomService.updateIntercom();
      });
    this.notificationStatus$ = this.getNotificationStatus();
    this.tourService.registerContainerRef(this.viewContainerRef);

    this.tourService.defineTour('scaffold-theme-setup', {
      setup: () => {
        if (this.sideNavState === 'collapsed') {
          this.sideNavState = 'compacted';
          this.mainSidebarService.updateState(true);
        }
        return new Promise<void>(resolve => {
          setTimeout(() => {
            const adminLink = document.querySelector(
              'a.nav-item-parent[href="/admin"]'
            ) as HTMLElement;
            if (adminLink) {
              const parentGroup = adminLink.closest('.nav-item-group');
              if (parentGroup && !parentGroup.classList.contains('expanded')) {
                const expandBtn = parentGroup.querySelector(
                  '.nav-expand-btn'
                ) as HTMLElement;
                if (expandBtn) {
                  expandBtn.click();
                }
              }
            }
            resolve();
          }, 300);
        });
      },
      setupDelay: 400,
      steps: [
        {
          stepId: 'step-1',
          title: 'Open Admin Menu',
          content: 'Expand the Admin menu to access styling options.',
          position: 'right-center',
          fastForward: true,
        },
        {
          stepId: 'step-2',
          title: 'Select Styling',
          content: 'Click Styling to customize your instance.',
          position: 'right-center',
          fastForward: true,
        },
        {
          stepId: 'step-3',
          title: 'Upload Logo',
          content: 'Upload a logo for your instance.',
          position: 'bottom',
          fastForward: false,
          canSkip: true,
        },
        {
          stepId: 'step-4',
          title: 'Select Colors',
          content:
            'Select the colors for the various elements in your instance.',
          position: 'top',
          fastForward: false,
        },
        {
          stepId: 'step-5',
          title: 'Review Changes',
          content: 'Review the look and accessibility of you changes.',
          position: 'top',
          fastForward: false,
          canSkip: true,
        },
        {
          stepId: 'step-6',
          title: 'Click Save',
          content: 'Click the Save button to apply the changes.',
          position: 'top',
          fastForward: false,
        },
        { stepId: 'step-7', end: true },
      ],
    });
    this.tourService.defineTour('create-integration', [
      {
        stepId: 'step-1',
        title: 'Scaffold Menu',
        content: 'Open the Scaffold menu.',
        position: 'left',
        fastForward: true,
      },
      {
        stepId: 'step-2',
        title: 'Select Imports',
        content: 'Select Imports from the navigation menu.',
        position: 'bottom-center',
        fastForward: true,
      },
      {
        stepId: 'step-3',
        title: 'Click New Integration',
        content:
          'Click the New Integration button to open the integration creation page.',
        position: 'top-center',
        fastForward: false,
      },
      {
        stepId: 'step-4',
        title: 'Enter Details',
        content: 'Enter the details to your LMS instance and utility account.',
        position: 'top-center',
        fastForward: false,
      },
      {
        stepId: 'step-5',
        title: 'Click Verify',
        content: 'Click the Verify button to verify the integration details.',
        position: 'left',
        fastForward: false,
      },
      {
        stepId: 'step-6',
        title: 'Enter Friendly Name',
        content: 'Enter a friendly name for the integration.',
        position: 'top',
        fastForward: false,
      },
      {
        stepId: 'step-7',
        title: 'Click Save',
        content: 'Click the Save button to create the integration.',
        position: 'top',
        fastForward: false,
      },
      { stepId: 'step-8', end: true },
    ]);
    this.tourService.defineTour('create-import', [
      {
        stepId: 'step-1',
        title: 'Scaffold Menu',
        content: 'Open the Scaffold menu.',
        position: 'left',
        fastForward: true,
      },
      {
        stepId: 'step-2',
        title: 'Select Imports',
        content: 'Select Imports from the navigation menu.',
        position: 'bottom-center',
        fastForward: true,
      },
      {
        stepId: 'step-3',
        title: 'Click New Import',
        content:
          'Click the New Import button to open the import creation page.',
        position: 'bottom-center',
        fastForward: false,
      },
      {
        stepId: 'step-4',
        title: 'Select Integration',
        content: 'Select the integration to use for the import.',
        position: 'right',
        fastForward: false,
      },
      {
        stepId: 'step-5',
        title: 'Select Import Type',
        content: 'Select how you want to import courses.',
        position: 'top',
        fastForward: false,
      },
      {
        stepId: 'step-6',
        title: 'Enter Details',
        content: 'Enter the details for the import.',
        position: 'top',
        fastForward: false,
      },
      {
        stepId: 'step-7',
        title: 'Click Start Import',
        content:
          'Click the Start Import button to begin importing the courses.',
        position: 'top',
        fastForward: false,
      },
      { stepId: 'step-8', end: true },
    ]);
    this.tourService.defineTour('inviting-users', [
      {
        stepId: 'step-1',
        title: 'Scaffold Menu',
        content: 'Open the Scaffold menu.',
        position: 'left',
        fastForward: true,
      },
      {
        stepId: 'step-2',
        title: 'Select Users & Roles',
        content: 'Select Users & Roles from the navigation menu.',
        position: 'left',
        fastForward: true,
      },
      {
        stepId: 'step-3',
        title: 'Click Add Users',
        content: 'Click the Add Users button to open the user creation page.',
        position: 'left',
        fastForward: false,
      },
      {
        stepId: 'step-4',
        title: 'Enter Email Address',
        content:
          'Enter the email addresses separated with a comma, for the users you are creating.',
        position: 'left',
        fastForward: false,
      },
      {
        stepId: 'step-5',
        title: 'Click Process',
        content:
          'Click the Process button to generate the list of users to create.',
        position: 'left',
        fastForward: false,
      },
      {
        stepId: 'step-6',
        title: 'Confirm User Details',
        content:
          'Confirm the details of the new users and make adjustments as needed.',
        position: 'left',
        fastForward: false,
        canSkip: true,
      },
      {
        stepId: 'step-7',
        title: 'Click Create Account',
        content: 'Click the Create Accounts button to create the new users.',
        position: 'left',
        fastForward: false,
      },
      { stepId: 'step-8', end: true },
    ]);
  }

  ngAfterViewInit() {
    // Prevent header button from being auto-focused on page refresh
    this.setupHeaderButtonFocusPrevention();
  }

  private setupHeaderButtonFocusPrevention() {
    if (!this.scaffoldMenuToggles || this.scaffoldMenuToggles.length === 0) {
      // Retry if button isn't available yet
      setTimeout(() => this.setupHeaderButtonFocusPrevention(), 100);
      return;
    }

    const buttonElement = this.scaffoldMenuToggles.first.nativeElement;
    // Find the actual button element (p-button wraps it)
    const actualButton =
      buttonElement?.querySelector('button') || buttonElement;

    if (!actualButton) {
      return;
    }

    // Set tabindex to -1 on the actual button element to prevent initial focus
    actualButton.setAttribute('tabindex', '-1');

    // Create focus handler that prevents auto-focus on page load
    const pageLoadTime = performance.now();
    this.headerButtonFocusHandler = (event: FocusEvent) => {
      // Only prevent focus if it happens within the first 3 seconds of page load
      // This allows normal user interaction after page loads
      const timeSinceLoad = performance.now() - pageLoadTime;
      if (timeSinceLoad < 3000) {
        event.stopPropagation();
        actualButton.blur();
        // Focus the body instead to remove focus from button
        if (document.body && typeof document.body.focus === 'function') {
          document.body.focus();
        } else {
          // Fallback: focus another element or just blur
          (document.activeElement as HTMLElement)?.blur();
        }
      }
    };

    // Add focus event listener with capture phase to catch early
    actualButton.addEventListener('focus', this.headerButtonFocusHandler, true);

    // Blur immediately if already focused
    const blurButton = () => {
      if (document.activeElement === actualButton) {
        actualButton.blur();
        if (document.body && typeof document.body.focus === 'function') {
          document.body.focus();
        } else {
          (document.activeElement as HTMLElement)?.blur();
        }
      }
    };

    // Try multiple times to catch delayed focus
    blurButton();
    setTimeout(blurButton, 0);
    setTimeout(blurButton, 50);
    setTimeout(blurButton, 100);
    setTimeout(blurButton, 200);
    setTimeout(blurButton, 500);
    setTimeout(blurButton, 1000);
    setTimeout(blurButton, 2000);

    // Also handle window load event
    if (window.addEventListener) {
      const windowLoadHandler = () => {
        blurButton();
        setTimeout(blurButton, 0);
        setTimeout(blurButton, 100);
        setTimeout(blurButton, 500);
      };
      if (document.readyState === 'complete') {
        windowLoadHandler();
      } else {
        window.addEventListener('load', windowLoadHandler, { once: true });
      }
    }

    // Restore tabindex after page load to allow keyboard navigation
    setTimeout(() => {
      actualButton.removeAttribute('tabindex');
      this.headerButtonTabIndex = 0;
      this.cd.detectChanges();
    }, 3000);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    // Clean up focus event listener
    if (
      this.headerButtonFocusHandler &&
      this.scaffoldMenuToggles &&
      this.scaffoldMenuToggles.length > 0
    ) {
      const buttonElement = this.scaffoldMenuToggles.first.nativeElement;
      const actualButton =
        buttonElement.querySelector('button') || buttonElement;
      if (actualButton) {
        actualButton.removeEventListener(
          'focus',
          this.headerButtonFocusHandler,
          true
        );
      }
    }
    if (this.preventFocusTimeout) {
      clearTimeout(this.preventFocusTimeout);
    }
  }

  setActiveFeature(event) {
    const newFeature = event?.value;
    this.environmentService.setActiveFeature(newFeature);
    this.activeFeature = newFeature;
    // Refresh Courses sub-nav for the new feature immediately
    // this.menuListService
    //   .patchCoursesMenuChildren(
    //     this.globalLeftMenuItems,
    //     this.instanceDetails,
    //     newFeature
    //   )
    //   .subscribe(() => this.cd.detectChanges());
    this.cd.detectChanges();
    this.setupSideMenu(true);
  }

  logout() {
    this.showLoadingContentOverlay = true;
    this.cd.markForCheck();
    // Deliberate logout, so any destination a guard recorded is no longer wanted.
    localStorage.removeItem(PENDING_REDIRECT_URL_KEY);
    // Prefer a cached cognito domain so a failed refetch cannot wipe it before
    // /sso/logout runs; fetch only when we have none yet, while still authenticated.
    this.cognitoDomainService
      .ensureCognitoDomain$()
      .pipe(
        take(1),
        switchMap(() => this.userService.logOut())
      )
      .subscribe(() => {
      this.sideNavState = this._navState[0];
      // if a cognitoSsoUrl has been set, it should send a logout request to cognito domain, unless user logged in outside of sso at /direct-login. if not a sso enabled instance, go to login page by default
      const authenticatedSso =
        this.cookieService.get('authenticated_sso') === 'true';
      const logouturl = this.cognitoSsoUrl
        ? !authenticatedSso || this.avoidSSORedirect
          ? ['/direct-login']
          : ['/sso/logout']
        : ['/login'];
      this.showLoadingContentOverlay = false;
      this.cd.markForCheck();
      this.router.navigate(logouturl);
    });
  }
  checkToOpenSidebar() {
    const until$ = new Subject();
    combineLatest([
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
      this.userService.getLogInState(),
    ])
      .pipe(takeUntil(until$))
      .subscribe(([event, loggedIn]) => {
        const urlAfterRedirects = (event as NavigationEnd).urlAfterRedirects;
        if (
          loggedIn &&
          urlAfterRedirects !== '/login' &&
          urlAfterRedirects !== '/verify-phone-and-email'
        ) {
          this.mainSidebarService.updateState(true);
          until$.next(null);
        }
      });
  }

  initSocket() {
    this.webSocketService.connect().subscribe();
  }

  initCognitoSSOLogic() {
    combineLatest([
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
      this.cognitoDomainService.getCognitoSsoUrl$(),
    ])
      .pipe(delay(10))
      .subscribe(([routerState, cognitoSsoUrl]: [NavigationEnd, string]) => {
        const [url, queryString] = routerState.urlAfterRedirects.split('?');
        this.cognitoSsoUrl = cognitoSsoUrl;
        // avoid displaying login page if meant to redirect
        if (
          url.includes('login') &&
          cognitoSsoUrl &&
          this.cookieService.get('authenticated_sso') === 'true' &&
          !this.avoidSSORedirect
        ) {
          this.overlay.open({ overlayStyles: { opacity: '.95' } }, 'ssoinit');
        }

        // if it ever comes across direct-login during the session, avoid redirecting to cognito domain for this session
        if (url.includes('direct-login')) {
          this.avoidSSORedirect = true;
          this.overlay.close('ssoinit');
        }

        if (
          this.cognitoSsoUrl &&
          !this.avoidSSORedirect &&
          !url.includes('sso/error')
        ) {
          // avoid setting redirect if sso pages or login pages after authenticated with sso
          if (!isAuthFlowUrl(url)) {
            localStorage.setItem('redirectUrl', url);
          } else {
            this.storeForwardUrlAsRedirect(queryString);
          }

          // let sso pages handle sso logic; login still hands off to Cognito.
          // Path-prefix so /admin/sso is not treated as an SSO handoff page.
          if (
            !isSsoFlowUrl(url) &&
            this.cookieService.get('authenticated') !== 'true'
          ) {
            window.location.href = this.cognitoSsoUrl;
          } else {
            // set to check valid session with idp every 5 minutes and set slo url
            if (!this.initalSsoAuthenticated) {
              // this.identityProviderService.getSloUrl$().subscribe()
              this.initalSsoAuthenticated = true;
              this.userService.startRefreshTokenTimer(5, true);
            }
          }
        }
      });
  }

  /**
   * IsAuthenticatedGuard bounces deep links to /login?forwardUrl=<destination>,
   * and only that redirected navigation emits a NavigationEnd. Carry the
   * destination over so SsoCheckComponent returns the user there after the SSO
   * round trip instead of falling back to /home.
   */
  private storeForwardUrlAsRedirect(queryString?: string): void {
    if (!queryString) {
      return;
    }
    const forwardUrl = new URLSearchParams(queryString).get('forwardUrl');
    if (forwardUrl && !isAuthFlowUrl(forwardUrl)) {
      localStorage.setItem(PENDING_REDIRECT_URL_KEY, forwardUrl);
    }
  }

  /** Toggle the Scaffold navigation menu */
  leftNavtoggle() {
    let cur = this._navState.indexOf(this.sideNavState);
    cur++;
    if (cur >= this._navState.length) {
      cur = 0;
    }
    console.log(cur, this._navState[cur]);
    this.sideNavState = this._navState[cur];
  }

  /**
   * Close the properties sidebar
   * @param e event
   */
  closeProperties(e) {
    if (!e.insideComponent) {
      this.properties.setProperties(null);
    }
  }
  /** Opens notification sidebar
   */
  openNotifications() {
    this.notificationService.showSidebar();
  }

  setInstance({ type, force }) {
    console.log('setInstance', type, force, this.instanceDetails);
    this.environmentService.setSelectedType(type);
    if (this.instanceDetails?.scaffoldType === type) return;
    if (this.router.url === '/home') {
      this.router
        .navigateByUrl('/refresh', { skipLocationChange: true })
        .then(() => {
          this.router.navigate(['/home']);
        });
    } else {
      if (!force) {
        this.router.navigate(['/home']);
      }
    }
    this.setupSideMenu();
  }

  /** Boot Intercom asynchronously so menu setup is not blocked by identity fetch. */
  private bootIntercomWithServerIdentity(roleData: any, instanceData: any): void {
    if (this.iframeService?.isInIframe || !instanceData || !roleData) {
      return;
    }
    const requestId = ++this.intercomBootRequestId;
    const institutionId = instanceData.id;
    this.userService
      .getIntercomUserHash$()
      .pipe(
        take(1),
        timeout(15000),
        catchError(() => of(null)),
        takeUntil(
          this.userService.getLogInState().pipe(filter(loggedIn => !loggedIn))
        )
      )
      .subscribe(intercomUserHash => {
        if (
          !this.userService.loggedIn ||
          requestId !== this.intercomBootRequestId ||
          this.instanceDetails?.id !== institutionId
        ) {
          return;
        }
        this.intercomService.initializeIntercom({
          roleData,
          instanceData,
          intercomUserHash,
        });
      });
  }

  private rebuildMenuForSystemTypeChange(details): void {
    this.instanceDetails = details;
    Sentry.setTag('self_serve', !details?.no_self_serve);
    this.userService
      .getRoleDetails()
      .pipe(take(1))
      .subscribe(roleData => {
        this.globalLeftMenuItems = this.menuListService.getGlobalMenu(
          details,
          roleData,
          this.activeFeature
        );
        this.footerMenu = this.menuListService.getMenuFooter(details, roleData);
        this.cd.detectChanges();
      });
  }

  setupSideMenu(nav = false) {
    this.userService.getLogInState().subscribe(loggedIn => {
      this.loadingMenu = true;
      console.log('LOG IN SUB', loggedIn);
      if (!loggedIn) {
        this.mainSidebarService.updateState(false);
        //this.sideNavState = "collapsed";
        //this.crispService.close();
        this.intercomService.shutdownIntercom();

        this.loggedIn = loggedIn;
        this.menuInitialized = false;
        // this.sideNavState = "collapsed";
        if (!this.iframeService?.isInIframe) {
          this.intercomService.shutdownIntercom();
        }
        this.loadingMenu = false;
      }
      if (loggedIn) {
        let roleData, instanceData;
        this.environmentService
          .getInstanceDetails(true)
          .pipe(take(1))
          .subscribe(data => {
            instanceData = data;
            Sentry.setTags({
              scaffoldType: instanceData?.scaffoldType,
              monday_project_board_name:
                instanceData?.monday_project_board_name || '',
              institution_id: instanceData?.id,
              institution_name: instanceData?.name,
              self_serve: !instanceData?.no_self_serve,
            });
            this.instanceDetails = instanceData;
            this.displayAutosave =
              instanceData.scaffoldType === 'designer' ||
              instanceData.scaffoldType === 'migration';
            if (instanceData.name) {
              this.pageTitleService.institution = instanceData.name;
            }
            this.userAttributes$ = this.userService
              .getCognitoUserAttributes$()
              .pipe(
                tap((attributes: any) => {
                  if (
                    this?.notifiedUnverifiedEmail ||
                    !attributes?.email ||
                    attributes?.email_verified === true ||
                    this.cognitoSsoUrl
                  )
                    return;
                  this.openUnverifiedNotification(attributes.email);
                })
              );
            this.userService
              .getRoleDetails(true)
              .pipe(take(1))
              .subscribe(data => {
                roleData = data;
                if (!this.iframeService?.isInIframe) {
                  this.bootIntercomWithServerIdentity(roleData, instanceData);
                } else if (this.isDarkMode) {
                  // avoid dark mode if in iframe
                  this.toggleTheme();
                }
                this.environmentService
                  .getFeatures()
                  .pipe(take(1))
                  .subscribe(availableFeatures => {
                    this.features = Features.filter(feature =>
                      availableFeatures.includes(feature.value)
                    ).filter(
                      feature => roleData['limit_' + feature.value] !== true
                    );
                    if (this.features.length > 1) {
                      this.features.push({
                        label: 'All',
                        value: 'all',
                      });
                    }
                    if (this.features.length > 0) {
                      this.environmentService
                        .getActiveFeature()
                        .pipe(take(1))
                        .subscribe(feature => {
                          let isAvaialble = this.features.some(
                            f => f.value === feature
                          );
                          if (isAvaialble) {
                            // if the current feature is available, set the active feature to the current feature
                            this.activeFeature = feature;
                          } else {
                            // if the current feature is not available, set the active feature to the first feature
                            this.environmentService.setActiveFeature(
                              this.features[0].value
                            );
                            this.activeFeature = this.features[0].value;
                          }
                          this.userService.setUserInfo(roleData.data);
                          this.userAvatarImageError = false;
                          this.globalLeftMenuItems =
                            this.menuListService.getGlobalMenu(
                              instanceData,
                              roleData,
                              this.activeFeature
                            );

                          if (!this.globalLeftMenuItems?.length) {
                            this.router.navigate(['error']);
                            this.globalLeftMenuItemsError = true;
                          }

                          if (nav && this.globalLeftMenuItems?.[0]?.link) {
                            this.router.navigate([
                              this.globalLeftMenuItems[0].link,
                            ]);
                          }
                          this.footerMenu = this.menuListService.getMenuFooter(
                            instanceData,
                            roleData
                          );
                          this.loadingMenu = false;
                          this.menuInitialized = true;
                          // this.menuListService
                          //   .patchCoursesMenuChildren(
                          //     this.globalLeftMenuItems,
                          //     instanceData,
                          //     this.activeFeature
                          //   )
                          //   .subscribe(() => this.cd.detectChanges());
                          // this.menuListService
                          //   .ensureCoursesViewCached(
                          //     this.activeFeature,
                          //     instanceData?.scaffoldType,
                          //     this.globalLeftMenuItems
                          //   )
                          //   .subscribe();
                          // this.cd.detectChanges();
                        });
                    } else {
                      this.userService.setUserInfo(roleData.data);
                      this.userAvatarImageError = false;
                      this.globalLeftMenuItems =
                        this.menuListService.getGlobalMenu(
                          instanceData,
                          roleData,
                          this.activeFeature
                        );

                      if (!this.globalLeftMenuItems?.length) {
                        this.router.navigate(['error']);
                        this.globalLeftMenuItemsError = true;
                      }

                      if (nav && this.globalLeftMenuItems?.[0]?.link) {
                        this.router.navigate([
                          this.globalLeftMenuItems[0].link,
                        ]);
                      }
                      this.footerMenu = this.menuListService.getMenuFooter(
                        instanceData,
                        roleData
                      );
                      this.loadingMenu = false;
                      this.menuInitialized = true;
                      // this.menuListService
                      //   .patchCoursesMenuChildren(
                      //     this.globalLeftMenuItems,
                      //     instanceData,
                      //     this.activeFeature
                      //   )
                      //   .subscribe(() => this.cd.detectChanges());
                      // this.menuListService
                      //   .ensureCoursesViewCached(
                      //     this.activeFeature,
                      //     instanceData?.scaffoldType,
                      //     this.globalLeftMenuItems
                      //   )
                      //   .subscribe();
                      this.cd.detectChanges();
                    }
                  });
              });
          });
      }
    });
  }

  checkForMfaEnabled() {
    this.mfacheck$ = this.userService.getLogInState().pipe(
      filter(loggedIn => !!loggedIn),
      switchMap(() => this.userService.getRoleDetails().pipe(take(1))),
      tap(() => this.userService.checkForMfaEnabled())
    );
  }

  openUnverifiedNotification(email) {
    this.notifiedUnverifiedEmail = true;
    const settings: NotificationMessageData = {
      icon: NotificationMessageIcon.email,
      text: `<strong>${this.translation.getTranslatedContent('unverified_email', 'Unverified Email')}:</strong> ${this.translation.getTranslatedContent('do_you_want_to_verify_this_email_now', 'Do you want to verify this email now')}? ${email}`,
      actionText: this.translation.getTranslatedContent('verify', 'Verify'),
      action: () => {
        let verifySettings: any = {
          context: {
            data: {
              contactType: 'email',
            },
          },
        };
        const dialogRef = this.dialog.open(
          VerifyModalComponent,
          verifySettings
        );
        dialogRef.onClose.subscribe(result => {
          if (result) {
            this.userInfo['email_verified'] = true;
            this.userService.setUserInfo(this.userInfo);
          }
        });
      },
    };
    this.notificationService.addUserNotification(settings, email);
  }

  getNotificationStatus() {
    return this.notificationService.getNotificationStatus().pipe(
      tap((status: NotificationStatus) => {
        this.activeNotificationCount = status.activeNotificationCount;
      })
    );
  }

  openFooterModal(modal) {
    if (!modal) return;
    switch (modal) {
      /*case "wizard": {
        this.wizardService.openWizard();
      }*/
      case 'quick_help': {
        this.dialog.open(QuickHelpComponent, {});
        break;
      }
    }
  }

  toggleTheme() {
    const isDarkMode =
      document.documentElement.classList.contains('dark-theme');
    if (isDarkMode) {
      document.documentElement.classList.remove('dark-theme');
      localStorage.setItem('forceTheme', 'light');
      this.isDarkMode = false;
    } else {
      document.documentElement.classList.add('dark-theme');
      localStorage.setItem('forceTheme', 'dark');
      this.isDarkMode = true;
    }
  }
}
